import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { BusinessShellNav } from './BusinessShellNav';
import { WorkspaceAccessState } from './WorkspaceAccessState';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { openAddressInMaps } from '@/lib/location/directions';
import { trackBusinessActionTapped } from '@/lib/analytics/posthog';
import { formatEventAgeRestriction } from '@/features/events/eventTags';
import { cn } from '@/lib/utils';
import { useBusinessWorkspace } from './useBusinessWorkspace';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import { getVenueCategoryConfig } from '@/features/venues/venueCategories';
import { Image } from 'expo-image';
import { Redirect, router } from 'expo-router';
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  Building2Icon,
  GlobeIcon,
  InstagramIcon,
  LockKeyholeIcon,
  MapPinIcon,
  PencilIcon,
  ShieldCheckIcon,
  UsersIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Linking, Pressable, ScrollView, View } from 'react-native';

const COUNT_FORMATTER = new Intl.NumberFormat();
const VENUE_HOURS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

export function WorkspaceOrganizationScene({
  organizationID,
  showSectionNav = true,
}: {
  organizationID: string;
  showSectionNav?: boolean;
}) {
  const session = useAuth((state) => state.session);
  const { organizationQuery, activeVenue, canManageVenues, orgAccessEnabled } =
    useBusinessWorkspace(organizationID);
  const venueDetailQuery = useVenueDetail(activeVenue?.venue_id ?? null);
  const venueDetail = venueDetailQuery.data;
  const categoryLabel =
    venueDetail?.category ? getVenueCategoryConfig(venueDetail.category).label : null;
  const trackVenueAction = React.useCallback(
    (action: Parameters<typeof trackBusinessActionTapped>[0]['action']) => {
      trackBusinessActionTapped({
        action,
        organizationID,
        source: 'business_venue',
        venueID: activeVenue?.venue_id ?? null,
      });
    },
    [activeVenue?.venue_id, organizationID]
  );

  if (!session) {
    return (
      <Redirect
        href={{
          pathname: '/sign-in',
          params: { next: `/workspaces/${organizationID}/organization` },
        }}
      />
    );
  }

  if (!orgAccessEnabled) {
    return <Redirect href={'/workspaces' as never} />;
  }

  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        {organizationQuery.isPending ? <LoadingState label="Loading venue..." /> : null}
        {organizationQuery.isError ? (
          <ErrorState
            body={getAPIErrorRecoveryCopy(
              organizationQuery.error,
              'workspace',
              'Unable to load venue.'
            )}
            onAction={() => organizationQuery.refetch()}
          />
        ) : null}

        {organizationQuery.data ? (
          <View className="gap-6">
            <BusinessShellNav
              organizationID={organizationID}
              organizationName={organizationQuery.data.name}
              viewerRole={organizationQuery.data.viewer_role}
              activeTab="organization"
              activeVenue={activeVenue}
              activeVenueVerified={venueDetail?.is_verified ?? false}
              showSectionNav={showSectionNav}
            />

            {organizationQuery.data.venues.length === 0 ? (
              <WorkspaceAccessState
                icon={
                  organizationQuery.data.viewer_role === 'owner' ? Building2Icon : LockKeyholeIcon
                }
                title={
                  organizationQuery.data.viewer_role === 'owner'
                    ? 'No venues yet'
                    : 'No venue access yet'
                }
                body={
                  organizationQuery.data.viewer_role === 'owner'
                    ? 'Finish organization setup and attach your first venue to open the full venue workspace.'
                    : 'You are in the organization, but no venue has been assigned to you yet. Ask an owner or manager to grant venue access.'
                }
              />
            ) : null}

            {activeVenue ? (
              <View className="gap-6">
                {venueDetailQuery.isPending ? (
                  <LoadingState label="Loading venue profile..." />
                ) : null}
                {venueDetailQuery.isError ? (
                  <ErrorState
                    body={getAPIErrorRecoveryCopy(
                      venueDetailQuery.error,
                      'venue',
                      'Unable to load venue details.'
                    )}
                    onAction={() => venueDetailQuery.refetch()}
                  />
                ) : null}

                {venueDetail ? (
                  <>
                    <VenuePresencePanel
                      ageRestriction={venueDetail.age_restriction}
                      categoryLabel={categoryLabel}
                      coverURL={venueDetail.cover_url}
                      dealCount={venueDetail.deals.length}
                      description={venueDetail.description}
                      eventCount={venueDetail.event_count}
                      followerCount={venueDetail.follower_count}
                      isVerified={venueDetail.is_verified}
                      logoURL={venueDetail.logo_url}
                      readiness={[
                        { complete: Boolean(venueDetail.cover_url || venueDetail.logo_url), label: 'Media' },
                        { complete: Boolean(venueDetail.address), label: 'Address' },
                        {
                          complete: Boolean(venueDetail.instagram_handle || venueDetail.website),
                          label: 'Links',
                        },
                        { complete: venueDetail.hours.length > 0, label: 'Hours' },
                      ]}
                    />

                    {/* Details — flat rows separated from snapshot by a rule */}
                    <View className="border-t border-border pt-5">
                      <SectionEyebrow>Details</SectionEyebrow>
                      <InfoRow
                        icon={MapPinIcon}
                        label="Address"
                        value={venueDetail.address || 'No address set'}
                        actionLabel={venueDetail.address ? 'Directions' : undefined}
                        onPress={
                          venueDetail.address
                            ? () => openAddressInMaps(venueDetail.address ?? '')
                            : undefined
                        }
                        first
                      />
                      <InfoRow
                        icon={GlobeIcon}
                        label="Website"
                        value={venueDetail.website || 'No website set'}
                        actionLabel={venueDetail.website ? 'Open' : undefined}
                        onPress={
                          venueDetail.website
                            ? () =>
                                Linking.openURL(
                                  /^https?:\/\//i.test(venueDetail.website ?? '')
                                    ? (venueDetail.website ?? '')
                                    : `https://${venueDetail.website ?? ''}`
                                ).catch(() => undefined)
                            : undefined
                        }
                      />
                      <InfoRow
                        icon={InstagramIcon}
                        label="Instagram"
                        value={
                          venueDetail.instagram_handle
                            ? `@${venueDetail.instagram_handle.replace(/^@/, '')}`
                            : 'No Instagram set'
                        }
                        actionLabel={venueDetail.instagram_handle ? 'Open' : undefined}
                        onPress={
                          venueDetail.instagram_handle
                            ? () =>
                                Linking.openURL(
                                  `https://instagram.com/${venueDetail.instagram_handle?.replace(/^@/, '') ?? ''}`
                                ).catch(() => undefined)
                            : undefined
                        }
                      />
                      <InfoRow
                        icon={venueDetail.is_verified ? ShieldCheckIcon : UsersIcon}
                        label="Status"
                        value={
                          venueDetail.is_verified ? 'Verified venue' : 'Public page is live'
                        }
                      />
                    </View>

                    {/* Hours — flat section */}
                    {venueDetail.hours.length > 0 ? (
                      <View className="border-t border-border pt-5">
                        <SectionEyebrow>Hours</SectionEyebrow>
                        <View>
                          {venueDetail.hours.map((hours, index) => (
                            <View
                              key={hours.day}
                              className={cn(
                                'flex-row items-center justify-between py-2.5',
                                index > 0 && 'border-t border-border/50'
                              )}>
                              <Text className="text-[14px] font-medium capitalize text-foreground">
                                {hours.day}
                              </Text>
                              <Text variant="small" className="text-muted-foreground">
                                {hours.closed
                                  ? 'Closed'
                                  : formatVenueHours(hours.opens_at, hours.closes_at)}
                              </Text>
                            </View>
                          ))}
                        </View>
                      </View>
                    ) : null}

                    {/* Actions — flat rows, no button components */}
                    <View className="border-t border-border/60">
                      {canManageVenues ? (
                        <Pressable
                          className="flex-row items-center gap-3.5 border-b border-border/40 py-3 active:bg-surface-alt/50"
                          onPress={() => {
                            trackVenueAction('edit_venue');
                            router.push(`/venues/${activeVenue.venue_id}/edit` as never);
                          }}>
                          <Icon as={PencilIcon} size={16} className="text-primary" />
                          <Text className="flex-1 text-[15px] font-medium text-foreground">
                            Edit venue profile
                          </Text>
                          <Icon as={ArrowRightIcon} size={13} className="text-muted-foreground/40" />
                        </Pressable>
                      ) : null}
                      <Pressable
                        className="flex-row items-center gap-3.5 py-3 active:bg-surface-alt/50"
                        onPress={() => {
                          trackVenueAction('view_live_page');
                          router.push(`/venues/${activeVenue.venue_id}` as never);
                        }}>
                        <Icon as={ArrowUpRightIcon} size={16} className="text-primary" />
                        <Text className="flex-1 text-[15px] font-medium text-foreground">
                          View live page
                        </Text>
                        <Icon as={ArrowRightIcon} size={13} className="text-muted-foreground/40" />
                      </Pressable>
                    </View>
                  </>
                ) : null}
              </View>
            ) : null}
          </View>
        ) : null}
      </ScrollView>
    </AppScreen>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </Text>
  );
}

function VenuePresencePanel({
  ageRestriction,
  categoryLabel,
  coverURL,
  dealCount,
  description,
  eventCount,
  followerCount,
  isVerified,
  logoURL,
  readiness,
}: {
  ageRestriction?: string | null;
  categoryLabel: string | null;
  coverURL?: string | null;
  dealCount: number;
  description?: string | null;
  eventCount: number;
  followerCount: number;
  isVerified: boolean;
  logoURL?: string | null;
  readiness: { complete: boolean; label: string }[];
}) {
  const cleanDescription = description?.trim();

  return (
    <View className="overflow-hidden rounded-md border border-border bg-card">
      <View className="h-36 bg-surface-alt">
        {coverURL ? (
          <Image source={{ uri: coverURL }} contentFit="cover" className="h-full w-full" />
        ) : logoURL ? (
          <View className="h-full items-center justify-center bg-surface-alt">
            <Image source={{ uri: logoURL }} contentFit="cover" className="size-24 rounded-md" />
          </View>
        ) : (
          <View className="h-full items-center justify-center bg-surface-alt">
            <View className="size-16 items-center justify-center rounded-md border border-primary-edge bg-primary-soft">
              <Icon as={Building2Icon} size={26} className="text-primary" />
            </View>
          </View>
        )}
      </View>

      <View className="gap-4 p-4">
        <View className="flex-row flex-wrap gap-2">
          <SignalPill label={isVerified ? 'Verified' : 'Live page'} tone="primary" />
          {categoryLabel ? <SignalPill label={categoryLabel} /> : null}
          {ageRestriction ? <SignalPill label={formatEventAgeRestriction(ageRestriction)} /> : null}
        </View>

        <Text className="text-[16px] font-semibold leading-6 text-foreground">
          {cleanDescription ||
            'Add a guest-facing description so people know what kind of night to expect.'}
        </Text>

        <View className="flex-row overflow-hidden rounded-md border border-border bg-background">
          <VenueStat value={formatCount(followerCount)} label="followers" />
          <VenueStat value={formatCount(eventCount)} label="events" divided />
          <VenueStat value={formatCount(dealCount)} label="deals" divided />
        </View>

        <View className="gap-2">
          <Text variant="small" className="font-semibold text-muted-foreground">
            Public profile
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {readiness.map((item) => (
              <ReadinessPill key={item.label} complete={item.complete} label={item.label} />
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}

function SignalPill({ label, tone = 'default' }: { label: string; tone?: 'default' | 'primary' }) {
  return (
    <View
      className={cn(
        'rounded-full border px-3 py-1',
        tone === 'primary' ? 'border-primary-edge bg-primary-soft' : 'border-border bg-background'
      )}>
      <Text
        variant="small"
        className={cn('font-semibold', tone === 'primary' ? 'text-primary' : 'text-foreground')}>
        {label}
      </Text>
    </View>
  );
}

function ReadinessPill({ complete, label }: { complete: boolean; label: string }) {
  return (
    <View
      className={cn(
        'rounded-full border px-2.5 py-1',
        complete ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-border bg-surface-alt'
      )}>
      <Text
        variant="small"
        className={cn(
          'text-[11px] font-semibold',
          complete ? 'text-emerald-400' : 'text-muted-foreground'
        )}>
        {complete ? 'Set' : 'Add'} {label}
      </Text>
    </View>
  );
}

function VenueStat({
  value,
  label,
  divided = false,
}: {
  value: string;
  label: string;
  divided?: boolean;
}) {
  return (
    <View className={cn('flex-1 gap-1 px-3 py-3', divided && 'border-l border-border/70')}>
      <Text className="text-[24px] font-bold leading-none text-foreground">{value}</Text>
      <Text variant="small" className="text-muted-foreground">
        {label}
      </Text>
    </View>
  );
}

function InfoRow({
  icon,
  label,
  value,
  actionLabel,
  onPress,
  first = false,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  value: string;
  actionLabel?: string;
  onPress?: () => void;
  first?: boolean;
}) {
  return (
    <View
      className={cn(
        'flex-row items-center gap-3 py-3',
        !first && 'border-t border-border/50'
      )}>
      <Icon as={icon} size={16} className="text-muted-foreground" />
      <View className="flex-1 gap-0.5">
        <Text variant="small" className="text-muted-foreground">
          {label}
        </Text>
        <Text className="text-[14px] font-medium text-foreground" numberOfLines={2}>
          {value}
        </Text>
      </View>
      {actionLabel && onPress ? (
        <Pressable accessibilityRole="button" className="py-1 active:opacity-60" onPress={onPress}>
          <Text className="text-[13px] font-medium text-primary">{actionLabel}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function formatCount(value: number) {
  return COUNT_FORMATTER.format(value);
}

function formatVenueHours(opensAt?: string | null, closesAt?: string | null) {
  if (!opensAt || !closesAt) {
    return 'Hours unavailable';
  }
  const start = new Date(`1970-01-01T${opensAt}`);
  const end = new Date(`1970-01-01T${closesAt}`);
  return `${VENUE_HOURS_FORMATTER.format(start)} – ${VENUE_HOURS_FORMATTER.format(end)}`;
}
